import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * MoodboardBlock — Inspirations visuelles dynamiques selon le type d'événement
 * Utilisé dans le configurateur (étape 1) et les pages thématiques
 */
export default function MoodboardBlock({ eventType, compact = false }) {
  const [data, setData] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (!eventType) return;
    fetch(`${BACKEND}/api/moodboard/${eventType}`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
  }, [eventType]);

  if (!data) return null;

  if (compact) {
    return (
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(212,175,55,0.25)', background: 'rgba(212,175,55,0.04)' }}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} style={{ color: '#D4AF37' }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#D4AF37' }}>
              Inspirations {data.titre}
            </p>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{data.ambiance}</p>
          {/* Palette couleurs */}
          <div className="flex gap-1.5 mb-3">
            {(data.couleurs || []).map((c, i) => (
              <div key={i} className="w-6 h-6 rounded-full border-2 border-white/20" style={{ background: c }} title={c} />
            ))}
          </div>
          {/* Thèmes */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(data.themes || []).slice(0, 4).map(t => (
              <button key={t}
                onClick={() => setSelectedTheme(selectedTheme === t ? null : t)}
                className="px-2.5 py-1 rounded-xl text-xs font-medium transition-all"
                style={{
                  background: selectedTheme === t ? '#D4AF37' : 'rgba(212,175,55,0.1)',
                  color: selectedTheme === t ? '#0B1E20' : '#C58B19',
                  border: 'none', cursor: 'pointer',
                }}>
                {t}
              </button>
            ))}
          </div>
          {/* Photos inspirations */}
          <div className="grid grid-cols-3 gap-1.5">
            {(data.inspirations || []).map((img, i) => (
              <div key={i} className="overflow-hidden rounded-lg" style={{ aspectRatio: '1/1' }}>
                <img src={img} alt={`Inspiration ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl overflow-hidden" style={{ background: '#0B1E20', border: '1px solid rgba(212,175,55,0.2)' }}>
      {/* Header */}
      <div className="p-6 border-b" style={{ borderColor: 'rgba(212,175,55,0.15)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={18} style={{ color: '#D4AF37' }} />
          <p className="font-display font-bold text-lg" style={{ color: '#FFFFFF' }}>{data.titre}</p>
        </div>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{data.ambiance}</p>
      </div>

      <div className="p-6">
        {/* Thèmes */}
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>Thèmes tendance</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {(data.themes || []).map(t => (
            <button key={t}
              onClick={() => setSelectedTheme(selectedTheme === t ? null : t)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: selectedTheme === t ? '#D4AF37' : 'rgba(255,255,255,0.08)',
                color: selectedTheme === t ? '#0B1E20' : 'rgba(255,255,255,0.7)',
                border: selectedTheme === t ? 'none' : '1px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
              }}>
              {t}
            </button>
          ))}
        </div>

        {/* Palette */}
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>Palette couleurs</p>
        <div className="flex gap-2 mb-5">
          {(data.couleurs || []).map((c, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full border-2" style={{ background: c, borderColor: 'rgba(255,255,255,0.2)' }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>{c}</span>
            </div>
          ))}
        </div>

        {/* Galerie inspirations */}
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>Inspirations visuelles</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {(data.inspirations || []).map((img, i) => (
            <div key={i} className="overflow-hidden rounded-xl" style={{ aspectRatio: '4/3' }}>
              <img src={img} alt={`Inspiration ${i + 1}`}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-400" loading="lazy" />
            </div>
          ))}
        </div>

        {/* Produits recommandés */}
        {(data.produits_recommandes || []).length > 0 && (
          <>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>Produits clés pour ce thème</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {(data.produits_recommandes || []).slice(0, 4).map(p => (
                <Link key={p.id} to={`/produit/${p.id}`}
                  className="flex items-center gap-2 p-2.5 rounded-xl hover:scale-105 transition-transform"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', textDecoration: 'none' }}>
                  {p.image ? (
                    <img src={p.image} alt={p.nom} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-xl" style={{ background: 'rgba(212,175,55,0.1)' }}>🎪</div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: '#FFFFFF' }}>{p.nom?.slice(0, 20)}</p>
                    {p.tarif > 0 && <p className="text-xs" style={{ color: '#D4AF37' }}>{p.tarif}€</p>}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <Link to="/creer-evenement"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
          style={{ background: '#D4AF37', color: '#0B1E20' }}>
          Composer cet événement <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
